import { useRouter as useAppRouter, usePathname, useSearchParams } from 'next/navigation';

import type { NextRouter } from 'next/router';
import { useRouter as useNextRouter } from 'next/router';

type RouterOptions = Parameters<NextRouter['push']>[2];

// Type for the shared router interface
interface SharedRouter {
  push: (url: string | URL, options?: RouterOptions) => Promise<boolean>;
  replace: (url: string | URL, options?: RouterOptions) => Promise<boolean>;
  back: () => void;
  pathname: string;
  query: Record<string, string>;
  isReady: boolean;
}

/**
 * A shared router hook that works with both App Router and Pages Router
 * Provides a consistent interface regardless of which router is being used
 */
export function useSharedRouter(): SharedRouter {
  const appRouter = useAppRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  try {
    // Try to use Pages Router first
    const pagesRouter = useNextRouter();

    return {
      push: (url: string | URL, options?: RouterOptions) => pagesRouter.push(url, undefined, options),
      replace: (url: string | URL, options?: RouterOptions) => pagesRouter.replace(url, undefined, options),
      back: pagesRouter.back,
      pathname: pagesRouter.pathname,
      query: pagesRouter.query as Record<string, string>,
      isReady: pagesRouter.isReady
    };
  } catch {
    // If Pages Router fails, we're in App Router
    // Convert searchParams to query object
    const query = Object.fromEntries(searchParams?.entries() ?? []);

    return {
      push: async (url: string | URL, options?: RouterOptions) => {
        appRouter.push(url.toString(), options);
        return true;
      },
      replace: async (url: string | URL, options?: RouterOptions) => {
        appRouter.replace(url.toString(), options);
        return true;
      },
      back: () => appRouter.back(),
      pathname: pathname ?? '',
      query,
      isReady: true // App Router is always ready
    };
  }
}
